import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { History, X } from 'lucide-react';
import {
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  UniversalEmptyState,
  UniversalLoadingState,
} from '@afrilink/ui';
import {
  adminCustomerService,
  type AdminAuditEntry,
} from '@/domains/customers/services/adminCustomerService';
import type { CustomerDetailContext } from './customerDetailTypes';

const ACTOR_TONE: Record<AdminAuditEntry['actor_kind'], StatusTone> = {
  customer: 'info',
  staff: 'beta',
  system: 'draft',
};

/**
 * Maps a dotted audit event name + its `after` payload into a short,
 * human-readable summary. Falls back to a title-cased version of the event
 * name so unknown events still render legibly. The raw event code is kept
 * visible (mono, smaller) so staff can still grep by it during incident
 * triage.
 */
function describeEvent(
  event: string,
  after: Record<string, unknown> | null | undefined
): string {
  const a = (after ?? {}) as Record<string, unknown>;
  const num = (k: string): string => {
    const v = a[k];
    if (v == null) return '';
    return typeof v === 'number' ? `R ${v.toFixed(2)}` : String(v);
  };
  const s = (k: string): string => {
    const v = a[k];
    return v == null ? '' : String(v);
  };

  switch (event) {
    case 'customer.created':
      return `Customer account created${s('email') ? ` for ${s('email')}` : ''}`;
    case 'customer.imported_from_whm':
      return `Imported from WHM (cPanel ${s('cpanel_username')})`;
    case 'customer.invite_sent':
    case 'customer.invite_resent':
      return `Portal invite sent to ${s('email')}`;
    case 'customer.password_reset_sent':
      return `Password reset email sent to ${s('email')}`;
    case 'whm.suspend_requested':
      return `Suspend requested for cPanel ${s('cpanel_username')}${s('reason') ? ` — ${s('reason')}` : ''}`;
    case 'whm.unsuspend_requested':
      return `Unsuspend requested for cPanel ${s('cpanel_username')}`;
    case 'whm.terminate_requested':
      return `Terminate requested for cPanel ${s('cpanel_username')}${s('reason') ? ` — ${s('reason')}` : ''}`;
    case 'whm.change_plan_requested':
      return `Plan change requested for cPanel ${s('cpanel_username')}`;
    case 'whm.change_password_requested':
      return `cPanel password reset requested for ${s('cpanel_username')}`;
    case 'whm.account_modified':
      return `WHM account modified — new package ${s('new_plan')}`;
    case 'whm.cpanel_sso':
      return `cPanel SSO link generated for ${s('cpanel_username')}`;
    case 'invoice.issued':
      return `Invoice ${s('invoice_number') || s('number')} issued — ${num('total')}`;
    case 'invoice.resent':
      return `Invoice ${s('invoice_number')} re-sent by email`;
    case 'invoice.seeded_historical':
      return `Invoice ${s('invoice_number')} seeded as paid (${num('total')}) — historical record`;
    case 'invoice.state_transition': {
      const before = ((a.lifecycle_state ?? '') as string) || '';
      const next = ((a.lifecycle_state ?? '') as string) || '';
      return `Invoice state changed → ${next || before || '?'}`;
    }
    case 'payment.received':
      return `Payment received — ${num('amount')} via ${s('gateway')}`;
    case 'payment.manual_recorded':
      return `Manual payment recorded — ${num('amount')} via ${s('gateway')} (invoice ${s('invoice_number')})`;
    case 'proof_of_payment.uploaded':
      return `Customer uploaded proof of payment for invoice ${s('invoice_number')}`;
    case 'proof_of_payment.verified':
      return `Proof of payment verified — invoice ${s('invoice_number')} marked paid`;
    case 'proof_of_payment.rejected':
      return `Proof of payment rejected${s('staff_note') ? ` — ${s('staff_note')}` : ''}`;
    case 'plan_change.confirmed':
      return `Plan change confirmed — invoice ${s('invoice_id') ? 'issued' : 'pending'}`;
    case 'ticket.created':
      return `Support ticket created${s('subject') ? ` — ${s('subject')}` : ''}`;
    case 'ticket.message_added':
      return `Reply added to support ticket`;
    case 'ticket.assigned':
      return `Ticket assigned`;
    case 'ticket.closed':
      return `Ticket closed`;
    case 'domain.registered':
      return `Domain ${s('domain_name')} registered`;
    case 'domain.renewed':
      return `Domain ${s('domain_name')} renewed`;
    case 'system_setting.updated':
      return `System setting "${s('key')}" updated`;
    default: {
      // Fallback: turn `foo.bar_baz` → "Foo · bar baz"
      const parts = event.split('.');
      const scope = parts[0] ?? event;
      const tail = parts.slice(1).join('.').replace(/_/g, ' ');
      return tail ? `${scope[0]?.toUpperCase() ?? ''}${scope.slice(1)} · ${tail}` : event;
    }
  }
}

export default function CustomerActivityTab() {
  const { customer } = useOutletContext<CustomerDetailContext>();
  const [entries, setEntries] = useState<AdminAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selected, setSelected] = useState<AdminAuditEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminCustomerService
      .getActivity(customer.customer_id, { limit: 200 })
      .then((data) => {
        if (!cancelled) {
          setEntries(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customer.customer_id]);

  if (loading) return <UniversalLoadingState variant="rows" count={6} />;
  if (error) {
    return (
      <NotificationCard
        tone="danger"
        title="Could not load activity"
        body={error.message}
      />
    );
  }
  if (entries.length === 0) {
    return (
      <Card interactive={false}>
        <UniversalEmptyState
          icon={History}
          title="No activity yet"
          description="Audit-log entries scoped to this customer will appear here."
        />
      </Card>
    );
  }

  return (
    <>
      <Card interactive={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-2">Time</th>
                <th className="px-6 py-2">Event</th>
                <th className="px-6 py-2">Actor</th>
                <th className="px-6 py-2">Entity</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer border-t border-border hover:bg-muted/60"
                  onClick={() => setSelected(e)}
                  tabIndex={0}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      setSelected(e);
                    }
                  }}
                >
                  <td className="whitespace-nowrap px-6 py-2 tabular-nums text-muted-foreground">
                    {new Date(e.created_at).toLocaleString('en-ZA')}
                  </td>
                  <td className="px-6 py-2 text-foreground">
                    <div className="text-sm">
                      {describeEvent(
                        e.event,
                        e.after as Record<string, unknown> | null
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {e.event}
                    </div>
                  </td>
                  <td className="px-6 py-2">
                    <StatusBadge tone={ACTOR_TONE[e.actor_kind]}>
                      {e.actor_kind}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-2 text-muted-foreground">
                    {e.entity_kind ? (
                      <>
                        {e.entity_kind}
                        {e.entity_id ? (
                          <span className="ml-1 text-xs">
                            {e.entity_id}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && (
        <AuditDrawer entry={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

/**
 * Audit drawer — slides in from the right, shows full event metadata plus
 * a side-by-side `before` / `after` JSON diff. Implementation note: a true
 * structural JSON diff (per-key highlighting) is deferred; for now we render
 * pretty-printed JSON in two columns, which is sufficient to debug staff
 * actions and matches the behaviour described in the implementation plan.
 */
function AuditDrawer({
  entry,
  onClose,
}: {
  entry: AdminAuditEntry;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-30 flex"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-scrim/40 backdrop-blur-sm"
        aria-hidden
      />
      <aside
        className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-card shadow-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Audit entry detail"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">
              {describeEvent(
                entry.event,
                entry.after as Record<string, unknown> | null
              )}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {entry.event}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Date(entry.created_at).toLocaleString('en-ZA')} ·{' '}
              {entry.actor_kind}
              {entry.ip ? ` · ${entry.ip}` : ''}
            </p>
            <p className="mt-0.5 text-[10px] text-muted-foreground/70">
              {entry.id}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <JsonBlock label="Before" value={entry.before} />
            <JsonBlock label="After" value={entry.after} />
          </div>
        </div>
      </aside>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const text = value === null || value === undefined ? '—' : safeStringify(value);
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <pre className="max-h-[60vh] overflow-auto rounded border border-border bg-muted p-3 text-xs leading-relaxed text-foreground">
        {text}
      </pre>
    </div>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
